// component-action-capture — Barre de capture d'action (piste 0.A).
//
// Zone de saisie libre en tête de la liste #actions. Tokenise `@personne`,
// `#rattachement`, `!priorité` et les dates FR, montre une preview de chips de
// confiance, et crée l'action (Entrée) ou l'ouvre pré-remplie dans l'éditeur
// (⇧Entrée / « Compléter »). Cf. docs/UX_BARRE_CAPTURE_ACTION.md.
//
// La logique DÉTERMINISTE (titre/date/priorité) vit dans domain/action-capture
// (pure + testée). Ce module ne porte que la glu DOM : caret/typeahead, chips,
// résolution des personnes/entités contre les référentiels, assemblage payload.
//
// Bare refs (résolues via core-globals.d.ts) : esc, t, toast, api, todayStr,
// fmtDate, priorityLabel, userLabel, trigramHtml, isUserActive.

import {
  parseCaptureText,
  buildCapturePayload,
  computeHighlights,
  type HighlightKind,
} from '../../domain/action-capture.js';

type Role = 'R' | 'A' | 'C' | 'I';

export interface CaptureUser {
  id: number;
  name: string;
  trigram?: string | null;
  is_team?: boolean;
  is_active?: boolean;
  departure_date?: string | null;
}
export interface CaptureProject {
  id: number;
  name?: string;
  code?: string | null;
}
export interface CaptureCommittee {
  id: number;
  code: string;
  name: string;
}
export interface CaptureStructure {
  id: number;
  code: string;
  label: string;
  is_active?: boolean;
}

export interface CaptureContext {
  users: ReadonlyArray<CaptureUser>;
  projects: ReadonlyArray<CaptureProject>;
  committees: ReadonlyArray<CaptureCommittee>;
  structures: ReadonlyArray<CaptureStructure>;
  defaultStatusId: number | null;
  // Rafraîchit la liste après création (typiquement router.dispatch()).
  onCreated: () => void;
  // Mode compact (header) : seul le champ est monté dans l'ancre ; la preview
  // + les boutons vivent dans un panneau flottant affiché au focus / à la saisie.
  compact?: boolean;
}

interface Person {
  id: number;
  name: string;
  trigram?: string | null;
  is_team?: boolean;
  role: Role;
}
type AttachKind = 'project' | 'committee' | 'structure';
interface Attach {
  kind: AttachKind;
  id: number;
  label: string;
}

const ROLE_LABELS: Record<Role, string> = { R: 'R', A: 'A', C: 'C', I: 'I' };

// Plafond de rendu du typeahead `#rattachement` (projets + comités +
// structures). Assez haut pour qu'un tenant normal voie TOUT en scrollant.
const MAX_ATTACH_OPTIONS = 50;

// Squelette DOM de la barre. `fieldRowHtml` = champ + bouton d'aide (toujours
// monté dans l'ancre) ; `bodyHtml` = preview + légende + CTA (dans l'ancre en
// mode plein, dans le panneau flottant en mode compact).
function captureSkeletonHtml(compact: boolean): { fieldRowHtml: string; bodyHtml: string } {
  const pad = compact ? '6px 10px' : '9px 11px';
  const fs = compact ? '14px' : '15px';
  return {
    fieldRowHtml: `
    <div class="acap-row" style="display:flex;align-items:center;gap:8px">
      <div class="acap-field" style="position:relative;flex:1;min-width:0;background:var(--bg);border:1px solid var(--border2);border-radius:var(--radius)">
        <div class="acap-hl" aria-hidden="true" style="position:absolute;inset:0;padding:${pad};font-size:${fs};line-height:20px;font-family:inherit;letter-spacing:normal;white-space:pre;overflow:hidden;color:var(--text);box-sizing:border-box;pointer-events:none"></div>
        <input class="acap-input" type="text" autocomplete="off"
          aria-label="${esc(t('actionCapture.aria'))}"
          style="position:relative;display:block;width:100%;font-size:${fs};line-height:20px;font-family:inherit;letter-spacing:normal;padding:${pad};border:none;outline:none;background:transparent;color:transparent;caret-color:var(--text);box-sizing:border-box">
      </div>
      <button class="acap-help" type="button" aria-haspopup="dialog"
        aria-label="${esc(t('actionCapture.help.title'))}" title="${esc(t('actionCapture.help.title'))}"
        style="width:28px;height:28px;flex-shrink:0;border-radius:50%;border:1px solid var(--border2);background:var(--accent-bg);color:var(--accent);cursor:pointer;font-size:14px;line-height:1">?</button>
    </div>`,
    bodyHtml: `
    <div class="acap-preview" style="display:flex;flex-wrap:wrap;align-items:center;gap:6px;margin-top:10px;min-height:8px"></div>
    <div class="acap-foot" style="display:flex;align-items:center;justify-content:space-between;gap:10px;margin-top:12px;padding-top:10px;border-top:1px solid var(--border)">
      <div class="acap-legend" style="display:flex;gap:14px;font-size:12px;color:var(--text3)">
        <span><b style="font-weight:600;color:var(--text)">@</b> ${esc(t('actionCapture.legend.person'))}</span>
        <span><b style="font-weight:600;color:var(--text)">#</b> ${esc(t('actionCapture.legend.attach'))}</span>
        <span><b style="font-weight:600;color:var(--text)">!</b> ${esc(t('actionCapture.legend.priority'))}</span>
      </div>
      <div style="display:flex;gap:8px;flex-shrink:0">
        <button class="acap-complete btn btn-secondary btn-sm" type="button">${esc(t('actionCapture.btn.complete'))}</button>
        <button class="acap-create btn btn-primary btn-sm" type="button">${esc(t('actionCapture.btn.create'))}</button>
      </div>
    </div>`,
  };
}

// Popover d'aide « ? » : mémo des sigils. Auto-suffisant (aucun état de la
// barre) — le 2e clic sur « ? » referme, un clic dehors aussi.
function toggleHelpPopup(helpBtn: HTMLButtonElement, closeAllPopups: () => void): void {
  const existing = document.querySelector('.acap-help-pop');
  if (existing) {
    existing.remove();
    return;
  }
  closeAllPopups();
  const pop = document.createElement('div');
  pop.className = 'acap-pop acap-help-pop';
  pop.setAttribute('role', 'dialog');
  pop.style.cssText =
    'position:fixed;z-index:1001;background:var(--bg2);border:1px solid var(--border2);border-radius:var(--radius);box-shadow:0 8px 24px rgba(0,0,0,.15);width:330px;overflow:hidden;font-size:12px';
  const line = (k: string, ex: string, d: string): string =>
    `<div style="display:flex;gap:10px;padding:7px 12px"><span style="min-width:22px;font-weight:600;color:var(--accent)">${esc(k)}</span><div><div style="color:var(--text)">${esc(ex)}</div><div style="color:var(--text3)">${esc(d)}</div></div></div>`;
  pop.innerHTML =
    `<div style="padding:9px 12px;border-bottom:1px solid var(--border);font-weight:600;color:var(--text)">${esc(t('actionCapture.help.title'))}</div>` +
    line('@', t('actionCapture.help.personEx'), t('actionCapture.help.personDetail')) +
    line('#', t('actionCapture.help.attachEx'), t('actionCapture.help.attachDetail')) +
    line('!', t('actionCapture.help.priorityEx'), t('actionCapture.help.priorityDetail')) +
    line('◷', t('actionCapture.help.dateEx'), t('actionCapture.help.dateDetail')) +
    `<div style="padding:8px 12px;border-top:1px solid var(--border);color:var(--text3)">${esc(t('actionCapture.help.submit'))}</div>`;
  document.body.appendChild(pop);
  const rect = helpBtn.getBoundingClientRect();
  pop.style.left = Math.max(8, rect.right - 330) + 'px';
  pop.style.top = rect.bottom + 6 + 'px';
  setTimeout(() => {
    const onOutside = (e: MouseEvent): void => {
      if (!pop.contains(e.target as Node) && e.target !== helpBtn) {
        document.removeEventListener('mousedown', onOutside, true);
        pop.remove();
      }
    };
    document.addEventListener('mousedown', onOutside, true);
  }, 0);
}

export function mountCaptureBar(anchor: HTMLElement, ctx: CaptureContext): void {
  const compact = ctx.compact === true;
  const people: Person[] = [];
  const attachments: Attach[] = [];
  let ownerId: number | null = null;
  let dropdownOpen = false;

  // Lu À CHAQUE ouverture du typeahead, jamais figé au montage : la barre du
  // header est montée une seule fois par session, un référentiel snapshotté y
  // resterait périmé jusqu'au prochain rechargement complet de la page.
  const activeUsers = (): CaptureUser[] => ctx.users.filter((u) => isUserActive(u as never));

  const { fieldRowHtml, bodyHtml } = captureSkeletonHtml(compact);

  const root = document.createElement('div');
  root.className = compact ? 'acap acap-compact' : 'acap';
  if (!compact) {
    root.style.cssText =
      'background:var(--bg2);border:1px solid var(--border2);border-radius:var(--radius);padding:10px 12px;margin-bottom:14px';
  }
  root.innerHTML = compact ? fieldRowHtml : fieldRowHtml + bodyHtml;
  anchor.insertAdjacentElement('afterbegin', root);

  // En mode compact, la preview + les boutons vivent dans un panneau flottant.
  let panel: HTMLElement | null = null;
  if (compact) {
    panel = document.createElement('div');
    panel.className = 'acap-panel';
    panel.style.cssText =
      'position:fixed;z-index:900;display:none;background:var(--bg2);border:1px solid var(--border2);border-radius:var(--radius);box-shadow:0 8px 24px rgba(0,0,0,.15);padding:10px 12px;width:460px;max-width:92vw';
    // eslint-disable-next-line no-restricted-syntax -- bodyHtml construit avec esc().
    panel.innerHTML = bodyHtml;
    document.body.appendChild(panel);
  }
  const host: HTMLElement = panel ?? root;

  const field = root.querySelector<HTMLElement>('.acap-field');
  const hl = root.querySelector<HTMLElement>('.acap-hl');
  const input = root.querySelector<HTMLInputElement>('.acap-input');
  const helpBtn = root.querySelector<HTMLButtonElement>('.acap-help');
  const preview = host.querySelector<HTMLElement>('.acap-preview');
  const createBtn = host.querySelector<HTMLButtonElement>('.acap-create');
  const completeBtn = host.querySelector<HTMLButtonElement>('.acap-complete');
  if (!field || !hl || !input || !helpBtn || !preview || !createBtn || !completeBtn) return;

  // ── Panneau flottant (mode compact) ─────────────────────────────────────────
  const positionPanel = (): void => {
    if (!panel) return;
    const r = field.getBoundingClientRect();
    const w = panel.offsetWidth || 460;
    let left = r.left;
    if (left + w > window.innerWidth - 8) left = window.innerWidth - w - 8;
    panel.style.left = Math.max(8, left) + 'px';
    panel.style.top = r.bottom + 6 + 'px';
  };
  let panelOutside: ((e: MouseEvent) => void) | null = null;
  const hidePanel = (): void => {
    if (!panel) return;
    panel.style.display = 'none';
    if (panelOutside) {
      document.removeEventListener('mousedown', panelOutside, true);
      panelOutside = null;
    }
  };
  const showPanel = (): void => {
    const p = panel;
    if (!p || p.style.display === 'block') return;
    p.style.display = 'block';
    positionPanel();
    const outside = (e: MouseEvent): void => {
      const target = e.target as Node | null;
      if (!target) return;
      if (p.contains(target) || root.contains(target)) return;
      if (target instanceof Element && target.closest('.acap-pop')) return;
      hidePanel();
    };
    panelOutside = outside;
    setTimeout(() => document.addEventListener('mousedown', outside, true), 0);
  };

  // ── Surlignage inline (backdrop miroir façon Todoist) ───────────────────────
  // Le backdrop rend le texte avec des spans colorés ; l'input (transparent,
  // caret seul) est posé dessus. On synchronise le scroll horizontal.
  const HL_STYLE: Record<HighlightKind, string> = {
    mention: 'background:var(--accent-bg);color:var(--accent);border-radius:3px',
    attach: 'background:var(--accent-bg);color:var(--accent);border-radius:3px',
    date: 'background:var(--accent-bg);color:var(--accent);border-radius:3px',
    priority: 'background:var(--warning-bg,#fdf0d5);color:var(--warning,#8a6d00);border-radius:3px',
  };
  const renderHighlight = (): void => {
    const val = input.value;
    if (val === '') {
      hl.innerHTML = `<span style="color:var(--text3)">${esc(t('actionCapture.placeholder'))}</span>`;
      return;
    }
    const segs = computeHighlights(val, todayStr());
    let html = '';
    let cursor = 0;
    for (const s of segs) {
      if (s.start < cursor) continue;
      html += esc(val.slice(cursor, s.start));
      html += `<span style="${HL_STYLE[s.kind]}">${esc(val.slice(s.start, s.end))}</span>`;
      cursor = s.end;
    }
    html += esc(val.slice(cursor));
    // eslint-disable-next-line no-restricted-syntax -- segments et texte échappés via esc().
    hl.innerHTML = html;
    hl.scrollLeft = input.scrollLeft;
  };

  // ── Owner : JAMAIS auto-assigné. On le conserve tant que la personne reste
  // Réalisateur ; sinon on l'efface. L'utilisateur le pose explicitement via le
  // menu de rôle (« Définir comme Owner »), et seul un Réalisateur peut l'être.
  const validateOwner = (): void => {
    if (ownerId != null && !people.some((p) => p.id === ownerId && p.role === 'R')) {
      ownerId = null;
    }
  };

  const setRole = (personId: number, role: Role): void => {
    const target = people.find((p) => p.id === personId);
    if (!target) return;
    // Un seul Approbateur : rétrograde l'ancien A en R (visible + note).
    if (role === 'A') {
      const prev = people.find((p) => p.role === 'A' && p.id !== personId);
      if (prev) {
        prev.role = 'R';
        toast(t('actionCapture.toast.singleApprover', { name: prev.name }), 'info');
      }
    }
    target.role = role;
    validateOwner();
    render();
  };

  const removePerson = (personId: number): void => {
    const i = people.findIndex((p) => p.id === personId);
    if (i >= 0) people.splice(i, 1);
    validateOwner();
    render();
  };

  const addPerson = (u: CaptureUser, role: Role): void => {
    if (!people.some((p) => p.id === u.id)) {
      people.push({
        id: u.id,
        name: u.name,
        trigram: u.trigram ?? null,
        is_team: u.is_team ?? false,
        role,
      });
    } else {
      const existing = people.find((p) => p.id === u.id);
      if (existing) existing.role = role;
    }
    validateOwner();
  };

  // Défaut visible : le créateur apparaît en RACI dès l'ouverture. L'action est
  // ainsi rattachée à lui (visible, plus de 404) et le défaut reste
  // modifiable/supprimable — pas d'affectation RACI cachée (cf. brief §9).
  //   • mode équipe : Réalisateur (R), il exécute et complètera la RACI ;
  //   • mode solo (US) : Approbateur (A) et RIEN d'autre. R/C/I n'ont pas de
  //     sens à un seul utilisateur — l'éditeur d'action les verrouille déjà
  //     (cf. component-action-modal), la barre de capture s'aligne.
  const seedCreator = (): void => {
    const meId = currentUserId();
    const me = meId != null ? ctx.users.find((u) => u.id === meId) : undefined;
    if (me) addPerson(me, window.isSoloUser?.() ? 'A' : 'R');
  };

  const addAttach = (a: Attach): void => {
    if (!attachments.some((x) => x.kind === a.kind && x.id === a.id)) attachments.push(a);
  };

  const removeAttach = (kind: AttachKind, id: number): void => {
    const i = attachments.findIndex((x) => x.kind === kind && x.id === id);
    if (i >= 0) attachments.splice(i, 1);
    render();
  };

  // ── Rendu de la preview de chips ────────────────────────────────────────────
  const chip = (bg: string, fg: string, inner: string): string =>
    `<span style="display:inline-flex;align-items:center;gap:5px;background:${bg};color:${fg};border-radius:999px;padding:3px 10px;font-size:12px">${inner}</span>`;

  const parseForPreview = (): ReturnType<typeof parseCaptureText> => {
    // Ignore un token @/# en cours de frappe pour ne pas polluer le titre.
    const raw = input.value.replace(/[@#][\p{L}\p{N}-]*$/u, '');
    return parseCaptureText(raw, todayStr());
  };

  const render = (): void => {
    renderHighlight();
    const parsed = parseForPreview();
    const parts: string[] = [];

    if (parsed.title) {
      parts.push(
        chip(
          'var(--bg3)',
          'var(--text)',
          `<span style="opacity:.6">${esc(t('actionCapture.chip.title'))}</span> ${esc(parsed.title)}`,
        ),
      );
    }

    for (const p of people) {
      const star = ownerId === p.id && p.role === 'R';
      const starIcon = star
        ? `<span title="${esc(t('actionCapture.owner'))}" style="color:var(--accent)">★</span>`
        : '';
      parts.push(
        `<span class="acap-person" data-pid="${p.id}" role="button" tabindex="0"
          title="${esc(t('actionCapture.chip.roleHint'))}"
          style="display:inline-flex;align-items:center;gap:5px;background:var(--accent-bg);color:var(--accent);border-radius:999px;padding:3px 6px 3px 10px;font-size:12px;cursor:pointer">
          ${starIcon}<b style="font-weight:600">${esc(ROLE_LABELS[p.role])}</b> ${esc(userLabel(p, true))}
          <button class="acap-rm-person" data-pid="${p.id}" aria-label="${esc(t('common.remove'))}"
            style="background:none;border:none;cursor:pointer;color:var(--accent);font-size:13px;padding:0 2px;line-height:1">×</button>
        </span>`,
      );
    }

    for (const a of attachments) {
      const badge = esc(t('actionCapture.attach.' + a.kind));
      parts.push(
        `<span style="display:inline-flex;align-items:center;gap:5px;background:var(--bg3);color:var(--text);border-radius:999px;padding:3px 6px 3px 10px;font-size:12px">
          <b style="font-weight:600;color:var(--text3)">${badge}</b> ${esc(a.label)}
          <button class="acap-rm-attach" data-kind="${a.kind}" data-id="${a.id}" aria-label="${esc(t('common.remove'))}"
            style="background:none;border:none;cursor:pointer;color:var(--text3);font-size:13px;padding:0 2px;line-height:1">×</button>
        </span>`,
      );
    }

    if (parsed.deadline) {
      const past = parsed.deadlinePast;
      parts.push(
        chip(
          past ? 'var(--warning-bg,#fff3cd)' : 'var(--accent-bg)',
          past ? 'var(--warning,#8a6d00)' : 'var(--accent)',
          `${esc(t('actionCapture.chip.deadline'))} ${esc(fmtDate(parsed.deadline))}${past ? ' · ' + esc(t('actionCapture.chip.past')) : ''}`,
        ),
      );
    } else if (parsed.invalidDate || parsed.ambiguousDate) {
      parts.push(chip('var(--bg3)', 'var(--text3)', esc(t('actionCapture.chip.dateUnrecognized'))));
    }

    if (parsed.priority != null) {
      parts.push(chip('var(--bg3)', 'var(--text)', esc(priorityLabel(parsed.priority))));
    }

    preview.innerHTML = parts.join('');

    preview.querySelectorAll<HTMLButtonElement>('.acap-rm-person').forEach((b) => {
      b.addEventListener('click', (e) => {
        e.stopPropagation();
        removePerson(parseInt(b.dataset['pid'] || '0'));
      });
    });
    preview.querySelectorAll<HTMLButtonElement>('.acap-rm-attach').forEach((b) => {
      b.addEventListener('click', (e) => {
        e.stopPropagation();
        removeAttach(
          (b.dataset['kind'] || 'project') as AttachKind,
          parseInt(b.dataset['id'] || '0'),
        );
      });
    });
    preview.querySelectorAll<HTMLElement>('.acap-person').forEach((el) => {
      el.addEventListener('click', (e) => {
        if ((e.target as HTMLElement).classList.contains('acap-rm-person')) return;
        openRoleMenu(el, parseInt(el.dataset['pid'] || '0'));
      });
    });
  };

  // ── Détection du token actif + typeahead ────────────────────────────────────
  const stripActiveToken = (): void => {
    const caret = input.selectionStart ?? input.value.length;
    const before = input.value.slice(0, caret);
    const m = /[@#][\p{L}\p{N}-]*$/u.exec(before);
    if (!m) return;
    const start = caret - m[0].length;
    input.value = input.value.slice(0, start) + input.value.slice(caret);
    input.setSelectionRange(start, start);
  };

  const closeAllPopups = (): void => {
    document.querySelectorAll('.acap-pop').forEach((el) => el.remove());
    dropdownOpen = false;
  };

  const onInput = (): void => {
    const caret = input.selectionStart ?? input.value.length;
    const before = input.value.slice(0, caret);
    const m = /([@#])([\p{L}\p{N}-]*)$/u.exec(before);
    if (m && m[1] === '@') {
      openPeopleTypeahead(m[2] ?? '');
    } else if (m && m[1] === '#') {
      openAttachTypeahead(m[2] ?? '');
    } else {
      closeAllPopups();
    }
    render();
  };

  const positionUnderInput = (el: HTMLElement, width: number): void => {
    const rect = input.getBoundingClientRect();
    const margin = 8;
    let left = rect.left;
    if (left + width > window.innerWidth - margin) left = window.innerWidth - width - margin;
    el.style.left = Math.max(margin, left) + 'px';
    el.style.top = rect.bottom + 4 + 'px';
  };

  const popupShell = (width: number): HTMLElement => {
    closeAllPopups();
    const dd = document.createElement('div');
    dd.className = 'acap-pop';
    dd.style.cssText = [
      'position:fixed',
      'z-index:1000',
      'background:var(--bg2)',
      'border:1px solid var(--border2)',
      'border-radius:var(--radius)',
      'box-shadow:0 8px 24px rgba(0,0,0,.15)',
      `width:${width}px`,
      'max-height:340px',
      'overflow-y:auto',
    ].join(';');
    document.body.appendChild(dd);
    positionUnderInput(dd, width);
    dropdownOpen = true;
    return dd;
  };

  const openPeopleTypeahead = (query: string): void => {
    const q = query.toLowerCase();
    const matches = activeUsers()
      .filter(
        (u) =>
          !q || u.name.toLowerCase().includes(q) || (u.trigram || '').toLowerCase().includes(q),
      )
      .slice(0, 8);
    const dd = popupShell(330);
    if (matches.length === 0) {
      dd.innerHTML = `<div style="padding:10px 12px;font-size:12px;color:var(--text3)">${esc(t('actionCapture.typeahead.noUser'))}</div>`;
      return;
    }
    const rows = matches
      .map(
        (u, i) => `<div class="acap-opt" data-idx="${i}" role="button" tabindex="-1"
        style="display:flex;align-items:center;gap:9px;padding:8px 12px;cursor:pointer;${i === 0 ? 'background:var(--accent-bg)' : ''}">
        ${trigramHtml(u, { extraStyle: 'font-size:10px;color:var(--text3);min-width:30px;display:inline-block', placeholder: '' })}
        <span style="flex:1;font-size:13px">${esc(u.name)}</span>
        ${i === 0 ? `<span style="font-size:11px;color:var(--accent)">↵ R</span>` : ''}
      </div>`,
      )
      .join('');
    dd.innerHTML =
      `<div style="padding:6px 12px;font-size:11px;color:var(--text3);border-bottom:1px solid var(--border)">${esc(t('actionCapture.typeahead.personHint'))}</div>` +
      rows;
    wireOptions(dd, matches.length, (idx, roleKey) => {
      const u = matches[idx];
      if (!u) return;
      addPerson(u, roleKey ?? 'R');
      stripActiveToken();
      closeAllPopups();
      render();
      input.focus();
    });
  };

  const openAttachTypeahead = (query: string): void => {
    const q = query.toLowerCase();
    type Opt = { kind: AttachKind; id: number; label: string; sub: string };
    const opts: Opt[] = [];
    for (const p of ctx.projects) {
      const label = p.name || p.code || '#' + p.id;
      if (!q || label.toLowerCase().includes(q) || (p.code || '').toLowerCase().includes(q)) {
        opts.push({ kind: 'project', id: p.id, label, sub: p.code || '' });
      }
    }
    for (const c of ctx.committees) {
      if (!q || c.name.toLowerCase().includes(q) || c.code.toLowerCase().includes(q)) {
        opts.push({ kind: 'committee', id: c.id, label: c.name, sub: c.code });
      }
    }
    for (const s of ctx.structures) {
      if (s.is_active === false) continue;
      if (!q || s.label.toLowerCase().includes(q) || s.code.toLowerCase().includes(q)) {
        opts.push({ kind: 'structure', id: s.id, label: s.label, sub: s.code });
      }
    }
    // `#` seul sert de « donne-moi la liste » : un plafond à 8 masquait les
    // projets au-delà (dont les derniers créés). Le popup scrolle déjà
    // (max-height + overflow-y), le plafond ne reste qu'un garde-fou de rendu.
    const shown = opts.slice(0, MAX_ATTACH_OPTIONS);
    const dd = popupShell(340);
    if (shown.length === 0) {
      dd.innerHTML = `<div style="padding:10px 12px;font-size:12px;color:var(--text3)">${esc(t('actionCapture.typeahead.noAttach'))}</div>`;
      return;
    }
    const rows = shown
      .map(
        (o, i) => `<div class="acap-opt" data-idx="${i}" role="button" tabindex="-1"
        style="display:flex;align-items:center;gap:9px;padding:8px 12px;cursor:pointer;${i === 0 ? 'background:var(--accent-bg)' : ''}">
        <span style="font-size:10px;padding:1px 7px;border-radius:4px;background:var(--bg3);color:var(--text3)">${esc(t('actionCapture.attach.' + o.kind))}</span>
        <span style="flex:1;font-size:13px">${esc(o.label)}</span>
        <span style="font-size:11px;color:var(--text3)">${esc(o.sub)}</span>
      </div>`,
      )
      .join('');
    // eslint-disable-next-line no-restricted-syntax -- rows construit avec esc() ci-dessus.
    dd.innerHTML = rows;
    wireOptions(dd, shown.length, (idx) => {
      const o = shown[idx];
      if (!o) return;
      addAttach({ kind: o.kind, id: o.id, label: o.label });
      stripActiveToken();
      closeAllPopups();
      render();
      input.focus();
    });
  };

  // Navigation clavier commune aux deux typeaheads. `onPick(idx, role?)`.
  const wireOptions = (
    dd: HTMLElement,
    count: number,
    onPick: (idx: number, role?: Role) => void,
  ): void => {
    let hi = 0;
    const paint = (): void => {
      dd.querySelectorAll<HTMLElement>('.acap-opt').forEach((el, i) => {
        el.style.background = i === hi ? 'var(--accent-bg)' : '';
        // La liste peut dépasser la hauteur du popup : garder la ligne
        // surlignée visible, sinon les flèches naviguent à l'aveugle.
        if (i === hi) el.scrollIntoView({ block: 'nearest' });
      });
    };
    dd.querySelectorAll<HTMLElement>('.acap-opt').forEach((el) => {
      el.addEventListener('mousedown', (e) => {
        e.preventDefault();
        onPick(parseInt(el.dataset['idx'] || '0'));
      });
    });
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        hi = (hi + 1) % count;
        paint();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        hi = (hi - 1 + count) % count;
        paint();
      } else if (e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        onPick(hi);
      } else if (e.key >= '1' && e.key <= '4') {
        e.preventDefault();
        onPick(hi, (['R', 'A', 'C', 'I'] as Role[])[parseInt(e.key) - 1]);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        closeAllPopups();
      }
    };
    input.addEventListener('keydown', onKey);
    // Retire le handler quand le popup disparaît.
    const obs = new MutationObserver(() => {
      if (!document.body.contains(dd)) {
        input.removeEventListener('keydown', onKey);
        obs.disconnect();
      }
    });
    obs.observe(document.body, { childList: true });
  };

  // ── Menu de rôle sur un chip personne ───────────────────────────────────────
  const openRoleMenu = (chipEl: HTMLElement, personId: number): void => {
    closeAllPopups();
    const person = people.find((p) => p.id === personId);
    if (!person) return;
    const dd = document.createElement('div');
    dd.className = 'acap-pop';
    dd.style.cssText =
      'position:fixed;z-index:1000;background:var(--bg2);border:1px solid var(--border2);border-radius:var(--radius);box-shadow:0 8px 24px rgba(0,0,0,.15);width:190px;overflow:hidden';
    const roleRow = (['R', 'A', 'C', 'I'] as Role[])
      .map(
        (r) => `<button data-role="${r}" type="button"
        style="display:block;width:100%;text-align:left;padding:7px 12px;background:${person.role === r ? 'var(--accent-bg)' : 'transparent'};border:none;cursor:pointer;font-size:12px;color:var(--text)">${esc(t('actionCapture.role.' + r))}</button>`,
      )
      .join('');
    const canSetOwner = person.role === 'R';
    dd.innerHTML =
      roleRow +
      `<div style="border-top:1px solid var(--border)"></div>` +
      (canSetOwner
        ? `<button data-set-owner="1" type="button" style="display:block;width:100%;text-align:left;padding:7px 12px;background:transparent;border:none;cursor:pointer;font-size:12px;color:var(--text)">★ ${esc(t('actionCapture.setOwner'))}</button>`
        : '') +
      `<button data-remove="1" type="button" style="display:block;width:100%;text-align:left;padding:7px 12px;background:transparent;border:none;cursor:pointer;font-size:12px;color:var(--danger,#b00)">${esc(t('common.remove'))}</button>`;
    document.body.appendChild(dd);
    const rect = chipEl.getBoundingClientRect();
    dd.style.left = Math.min(rect.left, window.innerWidth - 198) + 'px';
    dd.style.top = rect.bottom + 4 + 'px';
    dropdownOpen = true;
    dd.querySelectorAll<HTMLButtonElement>('button[data-role]').forEach((b) => {
      b.addEventListener('click', () => {
        closeAllPopups();
        setRole(personId, (b.dataset['role'] || 'R') as Role);
      });
    });
    dd.querySelector<HTMLButtonElement>('button[data-set-owner]')?.addEventListener('click', () => {
      ownerId = personId;
      closeAllPopups();
      render();
    });
    dd.querySelector<HTMLButtonElement>('button[data-remove]')?.addEventListener('click', () => {
      closeAllPopups();
      removePerson(personId);
    });
    setTimeout(() => {
      const onOutside = (e: MouseEvent): void => {
        if (!dd.contains(e.target as Node)) {
          document.removeEventListener('mousedown', onOutside, true);
          dd.remove();
          dropdownOpen = false;
        }
      };
      document.addEventListener('mousedown', onOutside, true);
    }, 0);
  };

  // ── Soumission ──────────────────────────────────────────────────────────────
  const buildPayload = (): Record<string, unknown> | null => {
    const parsed = parseCaptureText(input.value, todayStr());
    if (!parsed.title) {
      toast(t('actionCapture.toast.needTitle'), 'error');
      input.focus();
      return null;
    }
    return buildCapturePayload({
      parse: parsed,
      people,
      ownerId,
      attachments,
      defaultStatusId: ctx.defaultStatusId,
    });
  };

  const reset = (): void => {
    input.value = '';
    people.length = 0;
    attachments.length = 0;
    ownerId = null;
    seedCreator();
    hidePanel();
    render();
  };

  const submit = async (openEditor: boolean, btn: HTMLButtonElement): Promise<void> => {
    const payload = buildPayload();
    if (!payload) return;
    await api.withButton(btn, async () => {
      try {
        const created = (await api.createAction(payload, { notify: true })) as { id?: number };
        toast(t('actionCapture.toast.created'));
        // Remise à zéro dans TOUS les cas où l'action est bien créée, y compris
        // « Compléter » (⇧Entrée) : la barre du header survit à la navigation,
        // et laisser le texte en place exposait à une re-création au retour.
        reset();
        if (openEditor && created && created.id != null) {
          window.location.hash = '#action/' + created.id;
        } else {
          ctx.onCreated();
        }
      } catch (err: unknown) {
        toast(err instanceof Error ? err.message : t('common.error'), 'error');
      }
    });
  };

  // ── Wiring ──────────────────────────────────────────────────────────────────
  input.addEventListener('input', onInput);
  // Synchronise le défilement horizontal du backdrop quand le texte déborde.
  input.addEventListener('scroll', () => {
    hl.scrollLeft = input.scrollLeft;
  });
  // Anneau de focus porté par le wrapper (l'input n'a pas de bordure propre).
  input.addEventListener('focus', () => {
    field.style.boxShadow = '0 0 0 2px var(--accent-bg)';
    field.style.borderColor = 'var(--accent)';
    if (compact) showPanel();
  });
  input.addEventListener('blur', () => {
    field.style.boxShadow = '';
    field.style.borderColor = 'var(--border2)';
  });
  input.addEventListener('keydown', (e: KeyboardEvent) => {
    if (e.key === 'Enter' && !dropdownOpen) {
      e.preventDefault();
      void submit(e.shiftKey, e.shiftKey ? completeBtn : createBtn);
    } else if (e.key === 'Escape' && !dropdownOpen && compact) {
      hidePanel();
      input.blur();
    }
  });
  helpBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    toggleHelpPopup(helpBtn, closeAllPopups);
  });
  createBtn.addEventListener('click', () => void submit(false, createBtn));
  completeBtn.addEventListener('click', () => void submit(true, completeBtn));

  seedCreator();
  render();
}
